import { useState, useCallback, useEffect } from "react";
import {
  Sparkles,
  X,
  Trash2,
  Upload,
 
  Settings,
} from "lucide-react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import { EffectUploader } from "./EffectUploader";
import { CDN_URLS } from "../../../constants";
import { deeparEffectsAPI } from "../utils/deeparEffectsAPI";
import { toast } from "sonner";
import { IconButton } from "@mui/material";

// Sample effects - these are placeholders and would need actual .deepar files
const sampleEffects = [
  {
    name: "Aviators",
    path: CDN_URLS.AVIATORS_EFFECT,
    category: "filters",
  },
  {
    name: "Fire",
    path: CDN_URLS.FIRE_EFFECT,
    category: "filters",
  },
  {
    name: "NFL Helmet",
    path: CDN_URLS.NFL_HELMET_EFFECT,
    category: "filters",
  },
  // New effects
  {
    name: "Anime Hair",
    path: CDN_URLS.ANIME_HAIR_EFFECT,
    category: "filters",
  },
  {
    name: "Beard Cam",
    path: CDN_URLS.BEARD_CAM_EFFECT,
    category: "filters",
  },
  {
    name: "Big Head",
    path: CDN_URLS.BIG_HEAD_EFFECT,
    category: "filters",
  },
  {
    name: "Cowboy Hat",
    path: CDN_URLS.COWBOY_HAT_EFFECT,
    category: "filters",
  },
  {
    name: "Crazy Eyes",
    path: CDN_URLS.CRAZY_EYES_EFFECT,
    category: "filters",
  },
  {
    name: "Crown",
    path: CDN_URLS.CROWN_EFFECT,
    category: "filters",
  },
  {
    name: "Deal With It",
    path: CDN_URLS.DEALWITHIT_EFFECT,
    category: "filters",
  },
  {
    name: "Derp",
    path: CDN_URLS.DERP_EFFECT,
    category: "filters",
  },
  {
    name: "Face Swap",
    path: CDN_URLS.FACE_SWAP_EFFECT,
    category: "filters",
  },
  {
    name: "Fire Effect V2",
    path: CDN_URLS.FIRE_EFFECT_V2,
    category: "filters",
  },
  {
    name: "Harry Potter",
    path: CDN_URLS.HARRY_POTTER_EFFECT,
    category: "filters",
  },
  {
    name: "Heart Glasses",
    path: CDN_URLS.HEART_GLASSES_EFFECT,
    category: "filters",
  },
  {
    name: "Ice Head",
    path: CDN_URLS.ICE_HEAD_EFFECT,
    category: "filters",
  },
  {
    name: "Jags Helmet",
    path: CDN_URLS.JAGS_HELMET_EFFECT,
    category: "filters",
  },
  {
    name: "Lego Head",
    path: CDN_URLS.LEGO_HEAD_EFFECT,
    category: "filters",
  },
  {
    name: "Minecraft Creeper",
    path: CDN_URLS.MINECRAFT_CREEPER_EFFECT,
    category: "filters",
  },
  {
    name: "Minecraft Head",
    path: CDN_URLS.MINECRAFT_HEAD_EFFECT,
    category: "filters",
  },
  {
    name: "Mohawk",
    path: CDN_URLS.MOHAWK_EFFECT,
    category: "filters",
  },
  {
    name: "Mustache",
    path: CDN_URLS.MUSTACHE_EFFECT,
    category: "filters",
  },
  {
    name: "Neon Devil Horns",
    path: CDN_URLS.NEON_DEVIL_HORNS_EFFECT,
    category: "filters",
  },
  {
    name: "NHL Preds Helmet",
    path: CDN_URLS.NHL_PREDS_HELMET_EFFECT,
    category: "filters",
  },
  {
    name: "Paper Bag Head",
    path: CDN_URLS.PAPERBAG_HEAD_EFFECT,
    category: "filters",
  },
  {
    name: "Sad Face",
    path: CDN_URLS.SAD_FACE_EFFECT,
    category: "filters",
  },
  {
    name: "Santa",
    path: CDN_URLS.SANTA_EFFECT,
    category: "filters",
  },
  {
    name: "Vendetta",
    path: CDN_URLS.VENDETTA_EFFECT,
    category: "filters",
  },
  {
    name: "Viking Helmet",
    path: CDN_URLS.VIKING_HELMET_EFFECT,
    category: "filters",
  },
];

export const DeepARSettings = ({
  currentEffect,
  onEffectSelect,
  onEffectClear,
  isLoading,
  longDistanceTracking,
  onToggleLongDistanceTracking,
  onClose,
  eventId,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("filters");
  const [uploadedEffects, setUploadedEffects] = useState([]);
  const [isLoadingEffects, setIsLoadingEffects] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    effect: null,
  });

  // Fetch uploaded effects from database
  const fetchUploadedEffects = useCallback(async () => {
    if (!eventId) {
      console.log("[DeepARSettings] No eventId, skipping effects fetch");
      return;
    }

    console.log(
      `[DeepARSettings] Fetching uploaded effects for eventId: ${eventId}`
    );
    try {
      setIsLoadingEffects(true);
      const response = await deeparEffectsAPI.getEffects(eventId);
      console.log(`[DeepARSettings] API response:`, response);

      if (response.status === 200) {
        const effects = response.effects.map((effect) => ({
          _id: effect._id,
          name: effect.name,
          fileName: effect.fileName,
          category: effect.category,
          fileSize: effect.fileSize,
          effectFileUrl: effect.effectFileUrl, // Blob URL from backend
          isUploaded: true,
          eventId: effect.eventId,
          createdAt: effect.createdAt,
        }));
        console.log(
          `[DeepARSettings] Mapped ${effects.length} effects:`,
          effects
        );
        setUploadedEffects(effects);
      } else {
        console.warn(
          `[DeepARSettings] API returned non-200 status:`,
          response.status
        );
      }
    } catch (error) {
      console.error("Failed to fetch uploaded effects:", error);
      toast.error("Failed to load uploaded effects");
    } finally {
      setIsLoadingEffects(false);
    }
  }, [eventId]);

  // Load effects when component mounts or eventId changes
  useEffect(() => {
    fetchUploadedEffects();
  }, [fetchUploadedEffects]);

  // Update uploaded effects handler
  const handleEffectsUpdate = useCallback((newEffects) => {
    setUploadedEffects(newEffects);
  }, []);

  // Refresh effects list from server
  const refreshEffectsList = useCallback(async () => {
    console.log("[DeepARSettings] refreshEffectsList called");
    await fetchUploadedEffects();
    console.log("[DeepARSettings] refreshEffectsList completed");
  }, [fetchUploadedEffects]);

  // Handle delete effect with confirmation
  const handleDeleteEffect = useCallback((effect) => {
    setDeleteDialog({ open: true, effect });
  }, []);

  // Confirm delete effect
  const confirmDeleteEffect = useCallback(async () => {
    if (!deleteDialog.effect) return;

    try {
      await deeparEffectsAPI.deleteEffect(deleteDialog.effect._id);
      toast.success("Effect deleted successfully");

      // Remove from local state
      const updatedEffects = uploadedEffects.filter(
        (e) => e._id !== deleteDialog.effect._id
      );
      setUploadedEffects(updatedEffects);

      // If this effect was currently selected, clear it
      if (currentEffect === deleteDialog.effect.effectFileUrl) {
        onEffectClear();
      }
    } catch (error) {
      console.error("Failed to delete effect:", error);
      toast.error(`Failed to delete effect: ${error.message}`);
    } finally {
      setDeleteDialog({ open: false, effect: null });
    }
  }, [deleteDialog.effect, uploadedEffects, currentEffect, onEffectClear]);

  // Close delete dialog
  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, effect: null });
  }, []);

  // Handle effect selection - uploaded effects now use blob URLs directly
  const handleEffectSelect = useCallback(
    async (effectPath, effect) => {
      if (effect?.isUploaded && effect?.effectFileUrl) {
        try {
          // For uploaded effects, use the blob URL directly
          // The effectFileUrl is already an absolute URL from the backend
          onEffectSelect(effect.effectFileUrl);
        } catch (error) {
          console.error("Failed to load effect:", error);
          toast.error("Failed to load effect");
        }
      } else {
        // For CDN effects, use the path directly
        onEffectSelect(effectPath);
      }
    },
    [onEffectSelect]
  );

  // Combine sample effects with uploaded effects
  const allEffects = [...uploadedEffects, ...sampleEffects];

  const categorizedEffects = allEffects.reduce((acc, effect) => {
    if (!acc[effect.category]) {
      acc[effect.category] = [];
    }
    acc[effect.category].push(effect);
    return acc;
  }, {});

  const categories = [
    { id: "upload", label: "Upload", icon: Upload },
    { id: "filters", label: "Filters", icon: Sparkles },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl w-96 max-h-[600px] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700/50 bg-gray-800/50">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" style={{ color: "#24B8E7" }} />
          <h3 className="font-semibold text-lg text-white">DeepAR Effects</h3>
        </div>
        <IconButton sx={{ color: "white" }} onClick={onClose}>
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      {/* Content */}
      <div className="p-4 bg-gray-900/50">
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            sx={{
              borderBottom: 1,
              marginBottom: 2,
              borderColor: "rgba(156, 163, 175, 0.3)",
              "& .MuiTabs-indicator": {
                backgroundColor: "#24B8E7",
              },
            }}
          >
            {categories.map((category) => (
              <Tab
                key={category.id}
                value={category.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <category.icon className="h-4 w-4" />
                    {category.label}
                  </Box>
                }
                sx={{
                  fontSize: "0.75rem",
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: "white",
                  },
                }}
              />
            ))}
          </Tabs>
          {isLoading || !currentEffect ? null : (
            <Button
              onClick={onEffectClear}
              disabled={isLoading || !currentEffect}
              fullWidth
              startIcon={<Trash2 className="h-4 w-4" />}
              sx={{ my: 2, color: "gray" }}
              variant="outlined"
              color="secondary"
            >
              Clear Effect
            </Button>
          )}

          <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
            {/* Upload Tab */}
            {selectedCategory === "upload" && (
              <EffectUploader
                onEffectsUpdate={handleEffectsUpdate}
                uploadedEffects={uploadedEffects}
                eventId={eventId}
                isLoading={isLoadingEffects}
                onRefreshList={refreshEffectsList}
              />
            )}

            {/* Settings Tab */}
            {selectedCategory === "settings" && (
              <Box sx={{ py: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "white",
                  }}
                >
                  Face Tracking
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={longDistanceTracking}
                      onChange={onToggleLongDistanceTracking}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#3b82f6",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#3b82f6",
                          },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "medium",
                          color: "white",
                        }}
                      >
                        Long Distance Tracking
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          display: "block",
                        }}
                      >
                        Improved face detection for distant subjects
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 2, ml: 2 }}
                />
              </Box>
            )}

            {/* Filters Tab */}
            {selectedCategory === "filters" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  maxHeight: "300px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  width: "100%",
                  pr: 1, // Padding for scrollbar
                }}
              >
                {(categorizedEffects["filters"] || []).map((effect) => (
                  <Box
                    key={effect.path || effect._id || "none"}
                    sx={{
                      position: "relative",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleEffectSelect(effect.path, effect)}
                      disabled={isLoading}
                      fullWidth
                      title={effect.isUploaded ? "My Effect" : "Preloaded"}
                      color={effect.isUploaded ? "success" : "primary"}
                    >
                      {effect.name}
                    </Button>
                    {/* Delete Button for Uploaded Effects */}
                    {effect.isUploaded && (
                      <IconButton
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEffect(effect);
                        }}
                        sx={{
                          color: "gray",
                        }}
                      >
                        <Trash2 style={{ backgroundColor: "transparent" }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Trash2 className="h-5 w-5" />
            Delete Effect
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete the effect{" "}
            <strong>"{deleteDialog.effect?.name}"</strong>?
          </Typography>
          <Typography>
            This action cannot be undone. The effect file will be permanently
            removed from the server.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            onClick={confirmDeleteEffect}
            variant="contained"
            sx={{ color: "white" }}
            startIcon={<Trash2 className="h-4 w-4" />}
          >
            Delete Effect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      {isLoading && (
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
            Loading effect...
          </div>
        </div>
      )}
    </div>
  );
};
